import React, { FC } from 'react'
import ActionGroup from '@pixel/core/ActionGroup'
import Close from '@pixel/assets/icons/Close'
import Dialog from '@pixel/core/Dialog'
import IconButton from '@pixel/core/IconButton'
import { styled, useMediaQuery } from '@pixel/core/styles'
import LocalizedTypography from '../../components/LocalizedTypography'
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes'
import {AutomationSearchResult} from '../types/Automations'
import AutomationViewContainer from "./AutomationViewContainer";
import RunLogSection from "./RunLogSection";
import Typography from "@pixel/core/Typography";
import {ButtonProps} from "@pixel/core/Button";
import {useMultipleLocalizedText} from "../../contexts/Localization/localizationHooks";
import useAutomationUpdateQuery from "../hooks/useAutomationUpdateQuery";
import Tab from "@pixel/core/Tab";
import {TabContext, TabList, TabPanel} from "@mui/lab";
import VersionLogSection from "./VersionLogSection";

const WrappedDialogTitle = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(4),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
}))

const TabsContainer = styled('div')(({ theme }) => ({
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(4),
    backgroundColor: 'white',
    borderBottom: '1px solid #E4EDF3'
}))

const FlexCenterDiv = styled('div')({
    display: 'flex',
    alignItems: 'center',
})

const StickyHeader = styled('div')({
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: 'white'
});

const ScrollableContent = styled('div')({
    maxHeight: 'calc(100vh - 120px)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1
});

const automationStatusColorMap = {
    active: 'green' as 'success',
    inactive: 'error',
} as {
    [key in AutomationSearchResult['status']]: ButtonProps['color']
}

const automationStatusTranslationMap = {
    active: LocalizedText.AUTOMATION_STATUS_ACTIVE,
    inactive: LocalizedText.AUTOMATION_STATUS_INACTIVE,
} as {
    [key in AutomationSearchResult['status']]: LocalizedText
}

interface AutomationViewModalProps {
    open: boolean
    selectedAutomationId: number
    onAutomationSelected: (automation: any) => void
    changeAutomationStatus: (automation: any) => void
    onClose: () => void
}
export const AutomationViewModal: FC<AutomationViewModalProps> = ({
    open,
    selectedAutomationId,
    onAutomationSelected,
    changeAutomationStatus,
    onClose,
}) => {
    const matches = useMediaQuery('(max-width:700px)')

    const {
        data,
    } = useAutomationUpdateQuery({companyAutomationId: selectedAutomationId});


    const {
        AUTOMATION_STATUS_MAKE,
        AUTOMATION_STATUS_ACTIVE,
        AUTOMATION_STATUS_INACTIVE,
        AUTOMATION_EDIT,
        AUTOMATION_OVERVIEW,
        AUTOMATION_VERSION_LOG,
        AUTOMATION_RUN_LOG
    } = useMultipleLocalizedText()

    const [value, setValue] = React.useState('1');

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    const handleClose = () => {
        setValue('1');
        onClose();
    };

    if (!data) {
        return null
    }

    return (
        <Dialog
            id='automation-view'
            open={open}
            onClose={handleClose}
            fullScreen={matches}
            sx={{
                position: 'absolute',
                right: 0,
                left: 'unset',
                minWidth: matches ? '100vw' : 700,
            }}
            scroll="paper"
            PaperProps={{
                sx: { margin: 0, height: '100%', width: '100%', maxHeight: 'unset', maxWidth: 'unset' }
            }}
        >
            <TabContext value={value}>
                <StickyHeader>
                <WrappedDialogTitle>
                    <Typography id='automation-view-title' variant="h1">{data?.name ?? ''}</Typography>
                    <FlexCenterDiv>
                        <LocalizedTypography
                            id='automation-view-status-indicator'
                            color={automationStatusColorMap[data?.status.toLowerCase() as keyof typeof automationStatusColorMap]}
                            text={automationStatusTranslationMap[data?.status.toLowerCase() as keyof typeof automationStatusTranslationMap]}
                        />
                        <ActionGroup
                            id={`automation-form-action`}
                            actions={[
                                {
                                    label: AUTOMATION_STATUS_MAKE.replace(':status',
                                        data?.status !== 'ACTIVE' ?
                                            AUTOMATION_STATUS_ACTIVE :
                                            AUTOMATION_STATUS_INACTIVE
                                    ),
                                    onClick: () => {
                                        changeAutomationStatus({
                                            companyAutomationId: data.companyAutomationId,
                                            status: data.status
                                        })
                                    }
                                },
                                {
                                    label: AUTOMATION_EDIT,
                                    onClick: () => {
                                        onAutomationSelected(data)
                                    }
                                },
                            ]}
                        />
                        <IconButton id='automation-view-close-icon-button' aria-label="Close Modal" onClick={handleClose}>
                            <Close />
                        </IconButton>
                    </FlexCenterDiv>
                </WrappedDialogTitle>
                <TabsContainer>
                    <TabList onChange={handleChange}
                             textColor="inherit"
                             indicatorColor="secondary">
                        <Tab data-id='automation-view-tab-switcher-button-overview' label={AUTOMATION_OVERVIEW} value="1" />
                        <Tab data-id='automation-view-tab-switcher-button-version-log' label={AUTOMATION_VERSION_LOG} value="2" />
                        <Tab data-id='automation-view-tab-switcher-button-run-log' label={AUTOMATION_RUN_LOG} value="3" />
                    </TabList>
                </TabsContainer>
            </StickyHeader>
                <ScrollableContent>
                    <TabPanel value="1" sx={{paddingX: 0}}>
                        <AutomationViewContainer selectedAutomation={data} onModalClose={onClose} />
                    </TabPanel>
                    <TabPanel value="2" style={{flexGrow: 1, backgroundColor:  '#e4edf3'}}>
                        <VersionLogSection selectedAutomationId={selectedAutomationId} />
                    </TabPanel>
                    <TabPanel value="3" style={{ padding: 0, flexGrow: 1, backgroundColor: '#e4edf3' }}>
                        <RunLogSection companyAutomationId={data.companyAutomationId} />
                    </TabPanel>
                </ScrollableContent>
            </TabContext>
        </Dialog>
    )
}

export default AutomationViewModal
